import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { BackButton, Modal, ModalContent } from '../components/common';
import Button from '../components/common/Button';
import SectionHeader from '../components/common/SectionHeader';
import Typography from '../components/common/Typography';
import SimpleCourtAssignment from '../components/courts/SimpleCourtAssignment';
import { useSessionScoringConfig } from '../hooks/useSessionScoringConfig';
import CourtCountController from '../components/courts/CourtCountController';
import { createGame } from '../services/sessionService';
import { detectGameType } from '../utils/gameTypeUtils';
import { getPlayerNameColor } from '../utils/playerUtils';
import { 
  getSessionPlayers, 
  removePlayer, 
  endPrivateSession,
  addGuestPlayer,
  addClubPlayerToSession,
  addPlayerByEmail
} from '../services/privateSessionService';
import AddPlayerModal from '../components/private/AddPlayerModal';
import JoinCodeDisplay from '../components/private/JoinCodeDisplay';

const PrivateSessionDashboard = ({ currentUser, sessionId, sessionData }) => {
  const navigate = useNavigate();
  
  // State management
  const [session, setSession] = useState(sessionData);
  const scoringConfig = useSessionScoringConfig(session);
  const [players, setPlayers] = useState([]);
  const [courts, setCourts] = useState([{ courtNumber: 1, team1: [], team2: [], inProgress: false, winner: null, score: null }]);
  const [gameHistory, setGameHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [error, setError] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [numberOfCourts, setNumberOfCourts] = useState(1);
  const [layoutMode, setLayoutMode] = useState('auto');

  // Load session players
  const loadPlayers = useCallback(async () => {
    try {
      const sessionPlayers = await getSessionPlayers(sessionId);
      setPlayers(sessionPlayers);
    } catch (err) {
      console.error('Error loading players:', err);
    }
  }, [sessionId]);

  // Load game history
  const loadGameHistory = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('session_games')
        .select(`
          *,
          players_session_games (
            *,
            players:player_id (
              id,
              first_name,
              last_name,
              gender
            )
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process the data to structure teams properly
      const processedGames = (data || []).map(game => {
        const team1Players = [];
        const team2Players = [];
        
        if (game.players_session_games) {
          game.players_session_games.forEach(playerGame => {
            const playerData = {
              id: playerGame.players.id,
              first_name: playerGame.players.first_name,
              last_name: playerGame.players.last_name,
              gender: playerGame.players.gender
            };
            
            if (playerGame.team_number === 1) {
              team1Players.push(playerData);
            } else if (playerGame.team_number === 2) {
              team2Players.push(playerData);
            }
          });
        }
        
        return {
          ...game,
          team_1_players: team1Players,
          team_2_players: team2Players
        };
      });
      
      setGameHistory(processedGames);
    } catch (err) {
      console.error('Error loading game history:', err);
    }
  }, [sessionId]);

  // Load session data
  const loadSessionData = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          *,
          session_types:session_type_id (
            id,
            name,
            description
          ),
          host_player:host_player_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      if (!sessionData) throw new Error('Session not found');

      setSession(sessionData);
      setIsHost(sessionData.host_player_id === currentUser?.id);

      // Load players and game history
      await Promise.all([
        loadPlayers(),
        loadGameHistory()
      ]);

    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  }, [sessionId, currentUser?.id, loadPlayers, loadGameHistory]);

  // Initialize on mount
  useEffect(() => {
    loadSessionData();
  }, [loadSessionData]);

  // Handle adding players
  const handleAddPlayer = useCallback(async (playerData, method) => {
    try {
      switch (method) {
        case 'guest':
          await addGuestPlayer(sessionId, playerData, currentUser.id);
          break;
        case 'club':
          await addClubPlayerToSession(sessionId, playerData.id);
          break;
        case 'email':
          await addPlayerByEmail(sessionId, playerData.email, currentUser.id);
          break;
        default:
          throw new Error('Invalid player addition method');
      }
      
      await loadPlayers();
    } catch (err) {
      console.error('Error adding player:', err);
      setError(err.message || 'Failed to add player');
    }
  }, [sessionId, currentUser?.id, loadPlayers]);

  // Handle removing players
  const handleRemovePlayer = useCallback(async (playerId) => {
    if (!isHost) {
      setError('Only the host can remove players');
      return;
    }

    try {
      await removePlayer(sessionId, playerId, currentUser.id);
      await loadPlayers();
    } catch (err) {
      console.error('Error removing player:', err);
      setError(err.message || 'Failed to remove player');
    }
  }, [sessionId, currentUser?.id, isHost, loadPlayers]);

  // Create players by ID map
  const playersByIdMap = useMemo(() => {
    const map = {};
    players.forEach(player => {
      map[player.player_id] = player.players;
    });
    return map;
  }, [players]);

  // Clear court after game
  const clearCourt = useCallback((courtIndex) => {
    setCourts(prev => prev.map((court, index) => 
      index === courtIndex ? { 
        ...court, 
        team1: [], 
        team2: [], 
        inProgress: false, 
        winner: null, 
        score: null 
      } : court
    ));
  }, []);

  // Handle court count adjustment
  const handleAdjustCourtCount = useCallback((newCount) => {
    setNumberOfCourts(newCount);
    setCourts(prev => {
      const newCourts = [];
      for (let i = 1; i <= newCount; i++) {
        const existingCourt = prev.find(court => court.courtNumber === i);
        newCourts.push(existingCourt || { 
          courtNumber: i, 
          team1: [], 
          team2: [], 
          inProgress: false, 
          winner: null, 
          score: null 
        });
      }
      return newCourts;
    });
  }, []);

  // Get available players for courts
  const availablePlayers = useMemo(() => {
    return players
      .filter(p => p.status === 'active')
      .map(p => ({
        id: p.player_id,
        ...p.players
      }));
  }, [players]);

  // Handle adding player to court
  const handleAddPlayerToCourt = useCallback((playerId, courtNumber, teamNumber) => {
    console.log('🔥 handleAddPlayerToCourt called:', { playerId, courtNumber, teamNumber });
    
    setCourts(prev => prev.map((court, index) => {
      if (court.courtNumber !== courtNumber) return court;
      
      const newCourt = { ...court };
      const teamKey = teamNumber === 1 ? 'team1' : 'team2';
      
      // Check if player is already on this team
      if (!newCourt[teamKey].includes(playerId)) {
        // Remove player from any other court/team first
        const cleanedCourts = prev.map(c => ({
          ...c,
          team1: c.team1.filter(p => p !== playerId),
          team2: c.team2.filter(p => p !== playerId)
        }));
        
        // Add player to the specified team (store player ID, not object)
        cleanedCourts[index] = {
          ...cleanedCourts[index],
          [teamKey]: [...cleanedCourts[index][teamKey], playerId]
        };
        
        return cleanedCourts[index];
      }
      
      return newCourt;
    }));
  }, [availablePlayers]);

  // Handle removing a court
  const handleRemoveCourt = useCallback((courtIndex) => {
    if (numberOfCourts > 1) {
      setCourts(prev => {
        const filtered = prev.filter((_, index) => index !== courtIndex);
        // Renumber courts
        return filtered.map((court, index) => ({
          ...court,
          courtNumber: index + 1
        }));
      });
      setNumberOfCourts(prev => prev - 1);
    }
  }, [numberOfCourts]);

  // Handle score input
  const handleScoreInput = useCallback(async (courtIndex, scoreData) => {
    const court = courts[courtIndex];
    
    try {
      const gameData = {
        sessionId: sessionId,
        clubId: null,  // Private sessions have no club
        season_id: null,  // No season
        team_1_score: scoreData.team1Score,
        team_2_score: scoreData.team2Score,
        winningTeam: scoreData.winningTeam,
        team1Players: court.team1,
        team2Players: court.team2,
        game_type_id: detectGameType(court.team1, court.team2).id,
        gameCreateMethod: 'self_reported'  // Important: prevents ELO calculation (private sessions use self_reported)
      };
      
      await createGame(gameData, playersByIdMap, false);
      clearCourt(courtIndex);
      await loadGameHistory();
    } catch (err) {
      console.error('Error recording game:', err);
      setError(err.message || 'Failed to record game');
    }
  }, [sessionId, courts, playersByIdMap, clearCourt, loadGameHistory]);

  // Handle ending session
  const handleEndSession = useCallback(async () => {
    if (!isHost) {
      setError('Only the host can end the session');
      return;
    }

    try {
      await endPrivateSession(sessionId, currentUser.id);
      navigate('/');
    } catch (err) {
      console.error('Error ending session:', err);
      setError(err.message || 'Failed to end session');
    }
  }, [sessionId, currentUser?.id, isHost, navigate]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Typography variant="body">Loading private session...</Typography>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Typography variant="h3" className="text-danger-dark mb-4">Error</Typography>
          <Typography variant="body" className="text-text-secondary mb-4">{error}</Typography>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-sm border-b border-border-medium">
        <div className="max-w-app-shell mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <BackButton onClick={() => navigate('/private-sessions')} text="Private Sessions" />
              <div className="ml-4">
                <Typography variant="h4" className="font-semibold">
                  Private Session: {session?.name}
                </Typography>
                <Typography variant="body" className="text-text-secondary">
                  Host: {session?.host_player?.first_name} {session?.host_player?.last_name}
                </Typography>
              </div>
            </div>
            {isHost && (
              <div className="flex items-center space-x-4">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowEndSessionModal(true)}
                >
                  End Session
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-app-shell mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Session Info */}
        <div className="bg-surface rounded-lg shadow-sm border border-border-medium p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Typography variant="label" className="text-text-muted">Date</Typography>
              <Typography variant="body">{session?.session_date}</Typography>
            </div>
            <div>
              <Typography variant="label" className="text-text-muted">Time</Typography>
              <Typography variant="body">{session?.start_time} - {session?.end_time || 'Open'}</Typography>
            </div>
            <div>
              <Typography variant="label" className="text-text-muted">Location</Typography>
              <Typography variant="body">{session?.location || 'Not specified'}</Typography>
            </div>
          </div>
          {isHost && session?.join_code && (
            <div className="border-t border-border-medium pt-4">
              <JoinCodeDisplay joinCode={session?.join_code} />
            </div>
          )}
        </div>

        {/* Courts Section */}
        <div className="bg-surface rounded-lg shadow-sm border border-border-medium p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <SectionHeader title="Courts" />
            
            {/* Layout Controls */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Typography variant="body" className="text-text-secondary font-medium">
                  Layout:
                </Typography>
                <div className="flex space-x-1">
                  {['auto', '1', '2', '3', '4'].map((option) => (
                    <button
                      key={option}
                      onClick={() => setLayoutMode(option)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        layoutMode === option
                          ? 'bg-primary text-text-inverse'
                          : 'bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
                      }`}
                    >
                      {option === 'auto' ? 'Auto' : option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Court Count Controller */}
          <div className="mb-4">
            <CourtCountController
              currentCount={numberOfCourts}
              onIncrement={() => handleAdjustCourtCount(numberOfCourts + 1)}
              onDecrement={() => handleAdjustCourtCount(numberOfCourts - 1)}
              maxCourts={Math.max(1, Math.floor(availablePlayers.length / 4))}
              minCourts={1}
              disabled={false}
              courtAssignments={courts}
              availablePlayers={availablePlayers}
            />
          </div>
          
          <div className={`grid gap-4 ${
            layoutMode === 'auto' 
              ? numberOfCourts === 1 
                ? 'grid-cols-1 justify-center max-w-md mx-auto' 
                : numberOfCourts === 2 
                  ? 'grid-cols-1 lg:grid-cols-2' 
                  : numberOfCourts === 3 
                    ? 'grid-cols-1 lg:grid-cols-3' 
                    : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-4'
              : layoutMode === '1' 
                ? 'grid-cols-1 justify-center max-w-md mx-auto'
                : layoutMode === '2' 
                  ? 'grid-cols-1 lg:grid-cols-2'
                  : layoutMode === '3' 
                    ? 'grid-cols-1 lg:grid-cols-3'
                    : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-4'
          }`}>
            {courts.map((court, index) => (
              <SimpleCourtAssignment
                key={index}
                court={court}
                courtIndex={index}
                onUpdateResult={handleScoreInput}
                onRemoveCourt={handleRemoveCourt}
                onRemovePlayerFromCourt={(playerId, teamNumber, courtNumber) => {
                  setCourts(prev => prev.map((court, idx) => {
                    if (idx !== index) return court;
                    return {
                      ...court,
                      team1: court.team1.filter(p => p !== playerId),
                      team2: court.team2.filter(p => p !== playerId)
                    };
                  }));
                }}
                playersById={playersByIdMap}
                availablePlayers={availablePlayers}
                onAddPlayerToCourt={handleAddPlayerToCourt}
                scoringConfig={scoringConfig}
              />
            ))}
          </div>
        </div>

        {/* Players Section */}
        <div className="bg-surface rounded-lg shadow-sm border border-border-medium p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader title={`Players (${players.length})`} />
            <Button onClick={() => setShowAddPlayerModal(true)}>
              + Add Player
            </Button>
          </div>
          
          <div className={`grid gap-2 ${
            players.length > 6 
              ? 'grid-cols-1 lg:grid-cols-2' 
              : 'grid-cols-1'
          }`}>
            {players.map((player) => (
              <div key={player.id} className="flex items-center justify-between p-3 bg-surface-subtle rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-info-light rounded-full flex items-center justify-center">
                    <Typography variant="caption" className="font-semibold text-info-dark">
                      {player.players.first_name[0]}
                    </Typography>
                  </div>
                  <div>
                    <Typography 
                      variant="body" 
                      className={`font-medium ${getPlayerNameColor(player.players.gender)}`}
                    >
                      {player.players.first_name} {player.players.last_name}
                    </Typography>
                    <Typography variant="caption" className="text-text-muted">
                      {player.players.is_guest ? 'Guest' : 'Member'}
                    </Typography>
                  </div>
                </div>
                {isHost && (
                  <Button 
                    variant="secondary" 
                    size="small"
                    onClick={() => handleRemovePlayer(player.player_id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Game History */}
        {gameHistory.length > 0 && (
          <div className="bg-surface rounded-lg shadow-sm border border-border-medium p-6">
            <div className="flex items-center justify-between mb-4">
              <SectionHeader title={`Game History (${gameHistory.length} games)`} />
              <Button 
                variant="outline" 
                size="small"
                onClick={loadGameHistory}
              >
                Refresh
              </Button>
            </div>
            
            {/* Game History Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-medium">
                    <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">GAME</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-text-muted">TEAMS & SCORE</th>
                  </tr>
                </thead>
                <tbody>
                  {gameHistory.map((game, index) => {
                    const gameNumber = gameHistory.length - index;
                    
                    return (
                      <tr key={game.id} className="border-b border-border-medium">
                        <td className="py-6 px-4">
                          <div>
                            <Typography variant="h5" className="font-bold text-text-primary">
                              Game #{gameNumber}
                            </Typography>
                            <Typography variant="body" className="text-text-secondary font-medium">
                              {game.game_type || 'Doubles'}
                            </Typography>
                          </div>
                        </td>
                        
                        <td className="py-6 px-4">
                          <div className="flex items-center justify-between">
                            {/* Team 1 */}
                            <div className={`flex-1 p-4 rounded-l-lg ${game.winning_team === 1 ? 'bg-success-light/30' : 'bg-surface-subtle'}`}>
                              <div className="flex items-center justify-center space-x-2 mb-3">
                                <Typography variant="h6" className="font-semibold">
                                  Team 1
                                </Typography>
                                {game.winning_team === 1 && (
                                  <span className="text-warning text-lg">🏆</span>
                                )}
                              </div>
                              <div className="space-y-2">
                                {game.team_1_players && game.team_1_players.map((player, idx) => (
                                  <div key={idx} className="text-center">
                                    <Typography 
                                      variant="body" 
                                      className={`font-medium ${getPlayerNameColor(player.gender)}`}
                                    >
                                      {player.first_name} {player.last_name}
                                    </Typography>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Score */}
                            <div className="px-6 py-4 bg-surface border-l border-r border-border-medium">
                              <div className="flex items-center space-x-3">
                                <Typography variant="h4" className="font-bold text-text-primary">
                                  {game.team_1_score}
                                </Typography>
                                <Typography variant="body" className="text-text-muted font-medium">
                                  vs
                                </Typography>
                                <Typography variant="h4" className="font-bold text-text-primary">
                                  {game.team_2_score}
                                </Typography>
                              </div>
                            </div>
                            
                            {/* Team 2 */}
                            <div className={`flex-1 p-4 rounded-r-lg ${game.winning_team === 2 ? 'bg-success-light/30' : 'bg-surface-subtle'}`}>
                              <div className="flex items-center justify-center space-x-2 mb-3">
                                <Typography variant="h6" className="font-semibold">
                                  Team 2
                                </Typography>
                                {game.winning_team === 2 && (
                                  <span className="text-warning text-lg">🏆</span>
                                )}
                              </div>
                              <div className="space-y-2">
                                {game.team_2_players && game.team_2_players.map((player, idx) => (
                                  <div key={idx} className="text-center">
                                    <Typography 
                                      variant="body" 
                                      className={`font-medium ${getPlayerNameColor(player.gender)}`}
                                    >
                                      {player.first_name} {player.last_name}
                                    </Typography>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddPlayerModal && (
        <AddPlayerModal
          isOpen={showAddPlayerModal}
          onClose={() => setShowAddPlayerModal(false)}
          onAddPlayer={handleAddPlayer}
          sessionId={sessionId}
          currentUser={currentUser}
        />
      )}

      <Modal isOpen={showEndSessionModal} onClose={() => setShowEndSessionModal(false)}>
        <ModalContent size="small" constrainHeight={false}>
          <Typography variant="h5" className="font-semibold mb-4">
            End Session
          </Typography>
          <Typography variant="body" className="text-text-secondary mb-6">
            Are you sure you want to end this private session? This action cannot be undone.
          </Typography>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
            <Button variant="secondary" onClick={() => setShowEndSessionModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEndSession} className="flex-1">
              End Session
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-danger-light border border-danger text-danger-dark px-4 py-3 rounded">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-danger-dark hover:text-danger"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default PrivateSessionDashboard;
